/**
 * Raid AoE IMPACT presentation — fantasy ground-crack / eruption / arcane fan.
 * Covers full skill geometry. Distinct from telegraph range markers (yellow/red).
 */
import Phaser from "phaser";
import { coneTelegraphPoints } from "./combat-presentation";

export type RaidAoeTheme = "slam" | "brass" | "leap";

export type RaidAoeImpactOpts = {
  depth?: number;
  durationMs?: number;
};

type AoePalette = {
  crack: number;
  crackHot: number;
  primary: number;
  secondary: number;
  core: number;
  glow: number;
  ash: number;
};

/** Fantasy materials — never flat telegraph yellow/red alone. */
const PALETTES: Record<RaidAoeTheme, AoePalette> = {
  slam: {
    crack: 0x3d2416,
    crackHot: 0xb85a20,
    primary: 0xff6a1a,
    secondary: 0xff9940,
    core: 0xffe08a,
    glow: 0xffaa44,
    ash: 0x6b5344,
  },
  brass: {
    crack: 0x2a1850,
    crackHot: 0x7b3dff,
    primary: 0xa855ff,
    secondary: 0x44e8ff,
    core: 0xe8d0ff,
    glow: 0x66ccff,
    ash: 0x5a3a8a,
  },
  leap: {
    crack: 0x2a1210,
    crackHot: 0x8b2a18,
    primary: 0xff3b20,
    secondary: 0xff6620,
    core: 0xffaa55,
    glow: 0xff5520,
    ash: 0x5a4038,
  },
};

function pal(theme: RaidAoeTheme): AoePalette {
  return PALETTES[theme];
}

/** Deterministic pseudo-noise from index (no Math.random — replay-stable). */
function n01(i: number, salt = 0): number {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function shockwaveRing(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  tint: number,
  depth: number,
  duration: number,
  delay = 0,
  startScale = 0.1,
  lineW = 5,
  alphaFrom = 0.95,
): void {
  const r = Math.max(12, radius);
  const ring = scene.add
    .circle(x, y, r, tint, 0)
    .setStrokeStyle(lineW, tint, alphaFrom)
    .setDepth(depth)
    .setScale(startScale)
    .setAlpha(0);
  scene.tweens.add({
    targets: ring,
    scale: 1,
    alpha: { from: alphaFrom, to: 0 },
    duration,
    delay,
    ease: "Cubic.easeOut",
    onComplete: () => ring.destroy(),
  });
}

/**
 * Jagged ground-crack polylines from center toward rim (irregular, not uniform spokes).
 */
function jaggedCracks(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  palette: AoePalette,
  depth: number,
  count: number,
  duration: number,
  angleBias = 0,
  span = Math.PI * 2,
): void {
  const r = Math.max(12, radius);
  const g = scene.add.graphics().setDepth(depth);
  for (let i = 0; i < count; i++) {
    const u = i / Math.max(1, count);
    const jitter = (n01(i, 1) - 0.5) * 0.55;
    const a = angleBias - span * 0.5 + u * span + jitter;
    const lenMul = 0.55 + n01(i, 2) * 0.42;
    const segs = 4 + Math.floor(n01(i, 3) * 3);
    let px = x + Math.cos(a) * r * 0.06;
    let py = y + Math.sin(a) * r * 0.06;
    g.lineStyle(2.8 + n01(i, 4) * 1.4, palette.crack, 0.92);
    g.beginPath();
    g.moveTo(px, py);
    for (let s = 1; s <= segs; s++) {
      const t = s / segs;
      const base = r * lenMul * t;
      const side = (n01(i * 10 + s, 5) - 0.5) * r * 0.12 * (1 - t * 0.3);
      const nx = -Math.sin(a);
      const ny = Math.cos(a);
      const qx = x + Math.cos(a) * base + nx * side;
      const qy = y + Math.sin(a) * base + ny * side;
      g.lineTo(qx, qy);
      px = qx;
      py = qy;
    }
    g.strokePath();
    // Hot inner glow along same path (thinner)
    g.lineStyle(1.4, palette.crackHot, 0.75);
    g.beginPath();
    g.moveTo(x + Math.cos(a) * r * 0.08, y + Math.sin(a) * r * 0.08);
    g.lineTo(px, py);
    g.strokePath();
  }
  scene.tweens.add({
    targets: g,
    alpha: 0,
    duration: duration * 0.95,
    ease: "Quad.easeIn",
    onComplete: () => g.destroy(),
  });
}

/** Rising eruption / dust plume columns at rim points. */
function eruptionPlumes(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  palette: AoePalette,
  depth: number,
  count: number,
  duration: number,
  angleBias = 0,
  span = Math.PI * 2,
): void {
  const r = Math.max(12, radius);
  for (let i = 0; i < count; i++) {
    const u = count <= 1 ? 0.5 : i / (count - 1);
    const a = angleBias - span * 0.5 + u * span + (n01(i, 6) - 0.5) * 0.2;
    const dist = r * (0.72 + n01(i, 7) * 0.22);
    const sx = x + Math.cos(a) * dist;
    const sy = y + Math.sin(a) * dist;
    const h = 10 + n01(i, 8) * 16 + r * 0.06;
    const w = 3.5 + n01(i, 9) * 3;
    const col = i % 2 === 0 ? palette.primary : palette.secondary;
    const plume = scene.add
      .ellipse(sx, sy, w, h * 0.35, col, 0.85)
      .setDepth(depth)
      .setOrigin(0.5, 1);
    const ash = scene.add
      .ellipse(sx, sy - 2, w * 1.4, h * 0.2, palette.ash, 0.55)
      .setDepth(depth - 1)
      .setOrigin(0.5, 1);
    scene.tweens.add({
      targets: plume,
      scaleY: 2.6 + n01(i, 10) * 1.2,
      scaleX: 0.55,
      y: sy - h * 0.35,
      alpha: 0,
      duration: duration * (0.55 + n01(i, 11) * 0.25),
      delay: i * 18,
      ease: "Cubic.easeOut",
      onComplete: () => plume.destroy(),
    });
    scene.tweens.add({
      targets: ash,
      scaleY: 1.8,
      scaleX: 1.6,
      y: sy - h * 0.15,
      alpha: 0,
      duration: duration * 0.85,
      delay: i * 14,
      ease: "Quad.easeOut",
      onComplete: () => ash.destroy(),
    });
  }
}

/** Rock / ember debris flung outward. */
function debrisBurst(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  palette: AoePalette,
  depth: number,
  count: number,
  duration: number,
): void {
  const r = Math.max(12, radius);
  for (let i = 0; i < count; i++) {
    const a = (Math.PI * 2 * i) / count + n01(i, 12) * 0.35;
    const start = r * (0.15 + n01(i, 13) * 0.2);
    const end = r * (0.95 + n01(i, 14) * 0.35);
    const sx = x + Math.cos(a) * start;
    const sy = y + Math.sin(a) * start;
    const ex = x + Math.cos(a) * end;
    const ey = y + Math.sin(a) * end - (6 + n01(i, 15) * 14);
    const size = 1.6 + n01(i, 16) * 2.8;
    const col =
      i % 3 === 0 ? palette.ash : i % 3 === 1 ? palette.crackHot : palette.secondary;
    const p =
      i % 2 === 0
        ? scene.add.rectangle(sx, sy, size, size * 0.7, col, 1).setDepth(depth)
        : scene.add.circle(sx, sy, size * 0.55, col, 1).setDepth(depth);
    scene.tweens.add({
      targets: p,
      x: ex,
      y: ey,
      alpha: 0,
      scale: 0.25,
      angle: (n01(i, 17) - 0.5) * 220,
      duration: duration * (0.7 + n01(i, 18) * 0.25),
      ease: "Quad.easeOut",
      onComplete: () => p.destroy(),
    });
  }
}

/**
 * Circle AoE impact (slam / leap land) — jagged cracks + eruption plumes +
 * multi shock rings + debris. Duration ~600–800ms.
 */
export function playCircleAoeImpact(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  theme: RaidAoeTheme,
  opts: RaidAoeImpactOpts = {},
): void {
  const depth = opts.depth ?? 6;
  const duration = opts.durationMs ?? 720;
  const p = pal(theme);
  const r = Math.max(24, radius);

  // Hot core punch
  const core = scene.add
    .circle(x, y, Math.min(32, r * 0.24), p.core, 0.95)
    .setDepth(depth + 2);
  scene.tweens.add({
    targets: core,
    scale: 2.6,
    alpha: 0,
    duration: duration * 0.4,
    ease: "Cubic.easeOut",
    onComplete: () => core.destroy(),
  });
  const lavaCore = scene.add
    .circle(x, y, Math.min(22, r * 0.16), p.primary, 0.7)
    .setDepth(depth + 1);
  scene.tweens.add({
    targets: lavaCore,
    scale: 3.2,
    alpha: 0,
    duration: duration * 0.55,
    ease: "Cubic.easeOut",
    onComplete: () => lavaCore.destroy(),
  });

  jaggedCracks(scene, x, y, r, p, depth, 11, duration * 0.85);
  eruptionPlumes(scene, x, y, r, p, depth + 1, 7, duration);
  debrisBurst(scene, x, y, r, p, depth + 2, Math.round(16 + r / 10), duration);

  shockwaveRing(scene, x, y, r, p.glow, depth, duration, 0, 0.08, 7, 0.9);
  shockwaveRing(scene, x, y, r * 0.78, p.core, depth + 1, duration * 0.72, 50, 0.12, 3.5, 0.75);
  shockwaveRing(scene, x, y, r * 1.08, p.ash, depth - 1, duration * 0.95, 90, 0.2, 2.5, 0.45);

  // Ground scorched disk (dim, not telegraph fill)
  const scorch = scene.add
    .circle(x, y, r, p.crack, 0.28)
    .setDepth(depth - 2)
    .setScale(0.35);
  scene.tweens.add({
    targets: scorch,
    scale: 1.02,
    alpha: 0,
    duration: duration * 0.95,
    ease: "Cubic.easeOut",
    onComplete: () => scorch.destroy(),
  });
}

/**
 * Cone AoE impact (brass) — arcane floor eruption + ray cracks + outer arc burst.
 * Not a solid telegraph fill.
 */
export function playConeAoeImpact(
  scene: Phaser.Scene,
  x: number,
  y: number,
  aimAngle: number,
  range: number,
  coneDeg: number,
  theme: RaidAoeTheme = "brass",
  opts: RaidAoeImpactOpts = {},
): void {
  const depth = opts.depth ?? 6;
  const duration = opts.durationMs ?? 720;
  const p = pal(theme);
  const R = Math.max(40, range);
  const half = ((Math.max(12, coneDeg) * Math.PI) / 180) * 0.5;
  const span = half * 2;

  jaggedCracks(scene, x, y, R, p, depth, 8, duration * 0.8, aimAngle, span);

  // Floor eruption nodes along rays (not solid wedge fill)
  const rays = 7;
  for (let i = 0; i < rays; i++) {
    const u = i / (rays - 1);
    const a = aimAngle - half + u * span;
    for (let step = 1; step <= 4; step++) {
      const t = step / 4;
      const px = x + Math.cos(a) * R * t;
      const py = y + Math.sin(a) * R * t;
      const node = scene.add
        .ellipse(px, py, 4 + (1 - t) * 3, 6 + t * 8, step % 2 ? p.primary : p.secondary, 0.8)
        .setDepth(depth)
        .setOrigin(0.5, 1);
      scene.tweens.add({
        targets: node,
        scaleY: 2.4,
        scaleX: 0.5,
        y: py - 8 - t * 10,
        alpha: 0,
        duration: duration * (0.5 + t * 0.25),
        delay: i * 10 + step * 20,
        ease: "Cubic.easeOut",
        onComplete: () => node.destroy(),
      });
    }
    // Energy crack line
    const g = scene.add.graphics().setDepth(depth);
    g.lineStyle(2.4, p.crackHot, 0.9);
    const segs = 5;
    let lx = x;
    let ly = y;
    g.beginPath();
    g.moveTo(lx, ly);
    for (let s = 1; s <= segs; s++) {
      const t = s / segs;
      const base = R * t;
      const side = (n01(i * 7 + s, 20) - 0.5) * R * 0.06;
      const nx = -Math.sin(a);
      const ny = Math.cos(a);
      lx = x + Math.cos(a) * base + nx * side;
      ly = y + Math.sin(a) * base + ny * side;
      g.lineTo(lx, ly);
    }
    g.strokePath();
    g.lineStyle(1.2, p.secondary, 0.7);
    g.lineBetween(x, y, x + Math.cos(a) * R, y + Math.sin(a) * R);
    scene.tweens.add({
      targets: g,
      alpha: 0,
      duration: duration * (0.7 + u * 0.2),
      ease: "Quad.easeOut",
      onComplete: () => g.destroy(),
    });
  }

  // Outer arc burst
  const arcN = 14;
  for (let i = 0; i < arcN; i++) {
    const u = i / (arcN - 1);
    const a = aimAngle - half + u * span;
    const px = x + Math.cos(a) * R;
    const py = y + Math.sin(a) * R;
    const col = i % 2 === 0 ? p.secondary : p.core;
    const spark = scene.add.circle(px, py, 2.6 + n01(i, 21) * 1.4, col, 1).setDepth(depth + 1);
    scene.tweens.add({
      targets: spark,
      x: px + Math.cos(a) * (16 + n01(i, 22) * 14),
      y: py + Math.sin(a) * (16 + n01(i, 22) * 14),
      alpha: 0,
      scale: 0.2,
      duration: duration * 0.65,
      delay: i * 10,
      ease: "Quad.easeOut",
      onComplete: () => spark.destroy(),
    });
  }

  // Stroke-only wedge outline pulse (not telegraph fill)
  const pts = coneTelegraphPoints(x, y, aimAngle, R, coneDeg, 16);
  const outline = scene.add.graphics().setDepth(depth);
  outline.lineStyle(3.5, p.glow, 0.75);
  outline.beginPath();
  outline.moveTo(pts[0]!.x, pts[0]!.y);
  for (let i = 1; i < pts.length; i++) {
    outline.lineTo(pts[i]!.x, pts[i]!.y);
  }
  outline.closePath();
  outline.strokePath();
  scene.tweens.add({
    targets: outline,
    alpha: 0,
    duration,
    ease: "Cubic.easeOut",
    onComplete: () => outline.destroy(),
  });

  // Arcane muzzle flash
  const muzzle = scene.add.circle(x, y, 12, p.core, 0.95).setDepth(depth + 2);
  const muzzleRing = scene.add
    .circle(x, y, 18, p.secondary, 0)
    .setStrokeStyle(2.5, p.secondary, 0.85)
    .setDepth(depth + 2);
  scene.tweens.add({
    targets: muzzle,
    scale: 2.4,
    alpha: 0,
    duration: duration * 0.38,
    onComplete: () => muzzle.destroy(),
  });
  scene.tweens.add({
    targets: muzzleRing,
    scale: 2.8,
    alpha: 0,
    duration: duration * 0.5,
    onComplete: () => muzzleRing.destroy(),
  });
}

/** Cast-open cue: subtle rumble / crack foreshadow — not telegraph Graphics. */
export function playCircleAoeCastCue(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  theme: RaidAoeTheme,
  opts: RaidAoeImpactOpts = {},
): void {
  const depth = opts.depth ?? 6;
  const p = pal(theme);
  const r = Math.max(24, radius);
  jaggedCracks(scene, x, y, r * 0.55, p, depth, 5, 380);
  shockwaveRing(scene, x, y, r * 0.7, p.glow, depth, 360, 0, 0.45, 2.5, 0.4);
  const rumble = scene.add.circle(x, y, r * 0.2, p.crack, 0.35).setDepth(depth - 1);
  scene.tweens.add({
    targets: rumble,
    scale: 1.8,
    alpha: 0,
    duration: 320,
    ease: "Quad.easeOut",
    onComplete: () => rumble.destroy(),
  });
}

export function playConeAoeCastCue(
  scene: Phaser.Scene,
  x: number,
  y: number,
  aimAngle: number,
  range: number,
  coneDeg: number,
  theme: RaidAoeTheme = "brass",
  opts: RaidAoeImpactOpts = {},
): void {
  const depth = opts.depth ?? 6;
  const p = pal(theme);
  const R = Math.max(40, range);
  const half = ((Math.max(12, coneDeg) * Math.PI) / 180) * 0.5;
  jaggedCracks(scene, x, y, R * 0.5, p, depth, 4, 360, aimAngle, half * 2);
  for (let i = 0; i < 5; i++) {
    const u = i / 4;
    const a = aimAngle - half + u * half * 2;
    const p0 = scene.add
      .circle(x + Math.cos(a) * R * 0.25, y + Math.sin(a) * R * 0.25, 2.2, p.secondary, 0.85)
      .setDepth(depth);
    scene.tweens.add({
      targets: p0,
      x: x + Math.cos(a) * R * 0.75,
      y: y + Math.sin(a) * R * 0.75,
      alpha: 0,
      duration: 320,
      onComplete: () => p0.destroy(),
    });
  }
}
